import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useSelector } from 'react-redux';
import { Panel } from 'primereact/panel';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { Dropdown } from 'primereact/dropdown';
import { Loading } from '../../components/Loading';
import { growlShape } from '../../components/prop-types';
import { withGrowl } from '../../components/GrowlNotifications';
import { withAccount } from '../../components/AccountContext';
import { growlErrors } from '../../utils/NotificationUtil';
import TranslationComponent from './TranslationComponent';

const LOAD_DATA_ACTION_TYPE = 'LOAD_DATA';
const SET_LANGUAGE_ACTION_TYPE = 'SET_LANGUAGE';
const SET_PROJECT_ACTION_TYPE = 'SET_PROJECT';

const i18nMessages = defineMessages({
  updateTranslations: {
    id: 'menu.item-bank-management.update-translations',
    defaultMessage: 'Update Translations',
  },
  component: {
    id: 'menu.item-bank-management.component',
    defaultMessage: 'Component',
  },
  actions: {
    id: 'menu.item-bank-management.actions',
    defaultMessage: 'Actions',
  },
});

const orderAlphabetically = (a, b) => {
  if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
  if (b.label.toLowerCase() > a.label.toLowerCase()) return -1;
  return 0;
};

const TRANSLATION_PROJECT_QUERY = gql`
  query translationProjects($id: ID!) {
    translationProjects(id: $id) {
      urlSlug
      displayName
      components {
        urlSlug
        displayName
      }
    }
    activeLocales
  }
`;

const UpdateTranslations = ({ currentAccount, growl }) => {
  const {
    cldr: { localeDisplayName },
  } = useSelector(state => state.i18n);

  const { formatMessage } = useIntl();

  const initialState = {
    selectedLanguage: null,
    selectedTranslationProject: null,
    supportedLanguages: [],
    availableTranslationProjects: [],
    componentsToDisplay: [],
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case LOAD_DATA_ACTION_TYPE:
        return {
          selectedLanguage: action.payload.selectedLanguage,
          selectedTranslationProject: action.payload.selectedTranslationProject,
          supportedLanguages: action.payload.supportedLanguages,
          availableTranslationProjects: action.payload.availableTranslationProjects,
          componentsToDisplay: action.payload.componentsToDisplay,
        };
      case SET_LANGUAGE_ACTION_TYPE:
        return {
          ...state,
          selectedLanguage: action.payload.selectedLanguage,
        };
      case SET_PROJECT_ACTION_TYPE:
        return {
          ...state,
          selectedTranslationProject: action.payload.selectedTranslationProject,
          componentsToDisplay: action.payload.componentsToDisplay,
        };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const { loading, error, data } = useQuery(TRANSLATION_PROJECT_QUERY, { variables: { id: currentAccount } });

  const { activeLocales, translationProjects } = data;

  useEffect(() => {
    const dataIsAvailable = activeLocales
      && activeLocales.length > 0
      && translationProjects
      && translationProjects.length > 0;

    if (dataIsAvailable) {
      const languages = activeLocales
        .map(lang => ({
          value: lang,
          label: localeDisplayName(lang),
        }))
        .sort(orderAlphabetically);

      const projects = translationProjects.map(translationProject => ({
        value: translationProject.urlSlug,
        label: translationProject.displayName,
      }));

      const { components } = translationProjects.find(
        translationProject => translationProject.urlSlug === projects[0].value,
      );

      const action = {
        type: LOAD_DATA_ACTION_TYPE,
        payload: {
          selectedLanguage: languages[0].value,
          selectedTranslationProject: projects[0].value,
          supportedLanguages: languages,
          availableTranslationProjects: projects,
          componentsToDisplay: components,
        },
      };

      dispatch(action);
    }
  }, [activeLocales, translationProjects, localeDisplayName]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const {
    selectedLanguage,
    selectedTranslationProject,
    supportedLanguages,
    availableTranslationProjects,
    componentsToDisplay,
  } = state;

  return (
    <div className="brand-detailed-wrapper">
      <div className="wrapper p-grid">
        <div className="p-col-12">
          <Panel
            header={(
              <div className="update-translations-panel-header">
                <FormattedMessage
                  id="menu.item-bank-management.update-translations"
                  defaultMessage="Update Translations"
                />
                <div className="dropdowns">
                  <Dropdown
                    filter
                    filterBy="label"
                    value={selectedTranslationProject}
                    options={availableTranslationProjects}
                    onChange={(event) => {
                      const action = {
                        type: SET_PROJECT_ACTION_TYPE,
                        payload: {
                          selectedTranslationProject: event.value,
                          componentsToDisplay: translationProjects.find(
                            translationProject => translationProject.urlSlug === event.value,
                          ).components,
                        },
                      };

                      dispatch(action);
                    }}
                  />
                  <Dropdown
                    filter
                    filterBy="label"
                    value={selectedLanguage}
                    options={supportedLanguages}
                    onChange={(event) => {
                      const action = {
                        type: SET_LANGUAGE_ACTION_TYPE,
                        payload: {
                          selectedLanguage: event.value,
                        },
                      };

                      dispatch(action);
                    }}
                  />
                </div>
              </div>
            )}
          >
            <div className="update-translations-content">
              <div className="header">
                <div className="component-title">{formatMessage(i18nMessages.component)}</div>
                <div>{formatMessage(i18nMessages.actions)}</div>
              </div>
              <div className="components">
                {componentsToDisplay.map(component => (
                  <TranslationComponent
                    key={component.urlSlug}
                    translationProjectUrlSlug={selectedTranslationProject}
                    selectedLanguage={selectedLanguage}
                    translationComponent={component}
                    accountId={currentAccount}
                  />
                ))}
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

UpdateTranslations.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  growl: growlShape.isRequired,
};

export default withGrowl(withAccount(UpdateTranslations));
